/**
 * @fileoverview The quiet note when a subscriber has used most of their photo help (D23,
 * marketing's words): "You've used most of this month's photo help. Some
 * comes back on {date}; your plants, notes and warnings keep working as
 * always."
 *
 * On the screen they are using (the photo tray, the packet scan, the
 * check-up), when the server says it is due (`noteDue`: at 80% or more of
 * the allowance the garden in use spends, never for a trial, and not shown
 * to this person within the allowance's 30 days on any of their devices;
 * audit A71). Once drawn, the app tells the server (POST
 * /ai-allowance/note-shown), which keeps the mark on the account, and reads
 * the allowance again so no other screen shows it twice. Shown, it stays
 * while the screen is open. The date is the server's: the first day the
 * rolling use drops back under 80% as old uses age out.
 *
 * Where: the photo tray, the seed-packet scan and the plant check-up, near
 * their AI buttons.
 * API: GET /ai-allowance/garden (useAiAllowance('garden')), read again after
 * each AI use (refreshAiAllowance); POST /ai-allowance/note-shown
 * (markAiNearNoteShown). A mark that could not be kept is unseen by the
 * gardener, so a failure on our side is only reported to Sentry.
 * Settings: none on the device; the server's noteDue (its near share and
 * window).
 * Words (en/es): aiAllowance.near.
 * Watch out: once shown it is kept in state, so a later read that says not
 * due (as the read after the mark does) does not take it away while the
 * screen is open.
 *
 * @module AiNearNote
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { markAiNearNoteShown } from '@/shared/api/aiAllowance';
import { isServerFailure, reportServerFailure } from '@/shared/lib/serverFailure';
import { refreshAiAllowance, useAiAllowance } from '../lib/useAiAllowance';

/**
 * The note, when the server says it is due.
 *
 * @param props.enabled - False where the AI is not offered (no need to ask).
 * @param props.className - Extra classes for the box.
 */
export const AiNearNote: React.FC<{ enabled?: boolean; className?: string }> = ({ enabled = true, className }) => {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: allowance } = useAiAllowance('garden', enabled);
  /** Shown on this screen: kept while it is open, whatever the next read says. */
  const [shown, setShown] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (shown || !allowance?.noteDue || !allowance.comesBack) return;
    setShown(allowance.comesBack);
    markAiNearNoteShown()
      // Read again, so another screen's cached answer does not show it a second time.
      .then(() => refreshAiAllowance(queryClient))
      .catch((err: unknown) => {
        // Unseen by the gardener: a failure on our side is only reported.
        if (isServerFailure(err)) reportServerFailure(err, 'aiNearNote.shown');
      });
  }, [shown, allowance, queryClient]);

  if (!shown) return null;
  return (
    <p role="status" className={cn('rounded-sm border bg-card px-3 py-2 text-sm text-muted-foreground', className)}>
      {t('aiAllowance.near', { date: shown })}
    </p>
  );
};

/** The note, as a default import. */
export default AiNearNote;
